import { Stack, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

export function TransportationTable({
  rows,
}: {
  rows: {
    companyLogo?: string;
    flightNumber?: string;
    company?: string;
    departureTime?: string;
    departureDate: string;
    departure: string;
    arrivalTime?: string;
    arrivalDate: string;
    arrival: string;
    durationInMinutes?: string;
  }[];
}) {
  const { t } = useTranslation();

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: (t) => t.palette.secondary.dark,
              color: (t) => t.palette.secondary.contrastText,
            }}
          >
            <TableCell>
              <Typography color="white">
                {t('transportationTable.flight')}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography color="white">
                {t('transportationTable.departure')}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography color="white">
                {t('transportationTable.arrival')}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography color="white">
                {t('transportationTable.duration')}
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TransportationRow
              key={`${r.departure}-${r.arrival}-${i}`}
              {...r}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TransportationRow({
  companyLogo,
  flightNumber,
  company,
  departureTime,
  departureDate,
  departure,
  arrivalTime,
  arrivalDate,
  arrival,
  durationInMinutes,
}: {
  companyLogo?: string;
  flightNumber?: string;
  company?: string;
  departureTime?: string;
  departureDate: string;
  departure: string;
  arrivalTime?: string;
  arrivalDate: string;
  arrival: string;
  durationInMinutes?: string;
}) {
  const formatTime = (timeString: string) => {
    if (!timeString) {
      return '';
    }
    const [hours, minutes] = timeString.split(':').slice(0, 2);
    return `${hours}:${minutes}`;
  };

  const formatDuration = (duration?: string) => {
    if (!duration) return '';
    const totalMinutes = parseInt(duration, 10);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours} s ${minutes} dk` : `${minutes} dk`;
  };

  return (
    <TableRow>
      <TableCell width="25%">
        {companyLogo || flightNumber || company ? (
          <Fragment>
            <Stack direction="row" spacing={1}>
              {companyLogo && <img src={companyLogo} height={24} />}
              <Typography sx={{ mt: 2 }}>{flightNumber}</Typography>
            </Stack>
            <Typography sx={{ mt: 1 }} color="GrayText">
              {company}
            </Typography>
          </Fragment>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell width="25%">
        {departureTime && (
          <Typography fontWeight="600">{formatTime(departureTime)}</Typography>
        )}
        <Typography>
          {new Date(departureDate).toLocaleString('tr-TR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            weekday: 'long',
          } as Intl.DateTimeFormatOptions)}
        </Typography>
        <Typography color="GrayText">{departure}</Typography>
      </TableCell>
      <TableCell width="25%">
        {arrivalTime && (
          <Typography fontWeight="600">{formatTime(arrivalTime)}</Typography>
        )}
        <Typography>
          {new Date(arrivalDate).toLocaleString('tr-TR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            weekday: 'long',
          } as Intl.DateTimeFormatOptions)}
        </Typography>
        <Typography color="GrayText">{arrival}</Typography>
      </TableCell>
      <TableCell width="25%">
        {durationInMinutes ? formatDuration(durationInMinutes) : '-'}
      </TableCell>
    </TableRow>
  );
}
